import IconDelete from '@assets/icons/delete.svg?react';
import {
 Box,
 Button,
 Dialog,
 DialogActions,
 DialogContent,
 DialogTitle,
 FormControl,
 InputLabel,
 MenuItem,
 Select,
 TextField,
 Typography,
 useMediaQuery,
 useTheme,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import {
 Control,
 Controller,
 FieldErrors,
 useForm,
 UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useConfirmationDialog } from '../../contexts/useConfirmationDialog';
import themeOptions from '../../utils/theme';
import CloseButton from '../common/CloseButton';
import {
 DashboardWidgetType,
 WidgetDestPointNetoProps,
 WidgetInOutProductProps,
 WidgetProductStockProps,
 WidgetProgressProps,
} from './Dashboard.types';
import DashboardWidgetModalDate from './DashboardWidgetModalDate';
import DestPointNetoFilters from './widgetFilters/DestPointNetoFilters';
import DriversProgressFilters from './widgetFilters/DriversProgressFilters';
import InOutProductFilters from './widgetFilters/InOutProductFilters';
import ProductStockFilters from './widgetFilters/ProductStockFilters';

type WidgetFormData =
 | WidgetProgressProps
 | WidgetDestPointNetoProps
 | WidgetInOutProductProps
 | WidgetProductStockProps;

interface DashboardWidgetModalProps {
 open: boolean;
 onClose: () => void;
 onSubmit: (data: DashboardWidgetType) => void;
 onDelete?: (id: string) => void;
 initialWidget?: DashboardWidgetType | null;
}

const getDefaultTitle = (type: string, t: (key: string) => string): string => {
 const currentYear = new Date().getFullYear();
 const typeLabel = t(
  `dashboard.widgetTypes.${
   type === 'InOutProduct'
    ? 'inOutProduct'
    : type === 'ProductStock'
    ? 'productStock'
    : type
  }`,
 );
 return `${typeLabel} ${currentYear}`;
};

const createDefaultWidget = (
 t: (key: string) => string,
): WidgetProgressProps => {
 const now = new Date();
 const lastMonth = new Date(now);
 lastMonth.setMonth(now.getMonth() - 1);
 const formatDate = (date: Date) => date.toISOString().slice(0, 10);

 return {
  id: undefined,
  title: getDefaultTitle('driversProgress', t),
  w: 2,
  type: 'driversProgress',
  dateFrom: formatDate(lastMonth),
  dateTo: formatDate(now),
 };
};

const DashboardWidgetModal: React.FC<DashboardWidgetModalProps> = ({
 open,
 onClose,
 onSubmit,
 onDelete,
 initialWidget,
}) => {
 const { t } = useTranslation();
 const widgetTypeOptions = [
  {
   value: 'driversProgress',
   label: t('dashboard.widgetTypes.driversProgress'),
  },
  { value: 'destPointNeto', label: t('dashboard.widgetTypes.destPointNeto') },
  { value: 'InOutProduct', label: t('dashboard.widgetTypes.inOutProduct') },
  { value: 'ProductStock', label: t('dashboard.widgetTypes.productStock') },
 ];
 const th = useTheme();
 const isLargerThanMd = useMediaQuery(themeOptions().breakpoints.up('md'));
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();
 const defaultValues = useMemo(() => createDefaultWidget(t), [t]);

 const {
  control,
  handleSubmit,
  reset,
  watch,
  setValue,
  formState: { errors },
 } = useForm<WidgetFormData>({ defaultValues });

 const selectedType = watch('type', defaultValues.type);
 const currentTitle = watch('title', defaultValues.title);

 useEffect(() => {
  if (open) {
   reset(initialWidget ?? createDefaultWidget(t));
  }
 }, [initialWidget, open, reset, t]);

 useEffect(() => {
  if (open && !initialWidget && currentTitle !== '') {
   const allDefaultTitles = [
    getDefaultTitle('driversProgress', t),
    getDefaultTitle('destPointNeto', t),
    getDefaultTitle('InOutProduct', t),
    getDefaultTitle('ProductStock', t),
   ];
   const isDefaultTitle = allDefaultTitles.includes(currentTitle);

   if (isDefaultTitle) {
    const defaultTitleForType = getDefaultTitle(selectedType, t);
    setValue('title', defaultTitleForType, { shouldValidate: false });
   }
  }
 }, [selectedType, open, initialWidget, currentTitle, setValue, t]);

 const handleFormSubmit = (data: DashboardWidgetType) => {
  onSubmit(data);
  reset(createDefaultWidget(t));
  onClose();
 };

 const handleCancel = () => {
  reset(initialWidget ?? createDefaultWidget(t));
  onClose();
 };

 const handleDelete = () => {
  if (initialWidget?.id && onDelete) {
   openConfirmationDialog({
    title: t('global.delete'),
    message: t('global.deleteMessage'),
    onConfirm: () => {
     onDelete(initialWidget.id!);
     onClose();
    },
   });
  }
 };

 return (
  <Dialog
   open={open}
   onClose={onClose}
   fullWidth
   fullScreen={!isLargerThanMd}
   sx={{
    '& .MuiDialog-container': {
     '& .MuiPaper-root': { width: '100%', maxWidth: '550px' },
    },
   }}
  >
   <DialogTitle>
    {initialWidget ? t('dashboard.editWidget') : t('dashboard.addNewWidget')}
   </DialogTitle>

   <CloseButton closeDialog={onClose} />

   <DialogContent sx={{ py: 1 }}>
    <form id="dashboard-widget-form" onSubmit={handleSubmit(handleFormSubmit)}>
     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
       sx={{
        borderRadius: '4px',
        p: 1,
        backgroundColor: (theme) => theme.palette.kemek.lightBlue,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
       }}
      >
       <FormControl fullWidth error={!!errors.type}>
        <InputLabel id="widget-type-label">
         {t('dashboard.widgetType')}
        </InputLabel>
        <Controller
         name="type"
         control={control}
         rules={{ required: t('dashboard.widgetTypeRequired') }}
         render={({ field }) => (
          <Select
           {...field}
           labelId="widget-type-label"
           id="widget-type"
           label={t('dashboard.widgetType')}
           sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
          >
           {widgetTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
             {option.label}
            </MenuItem>
           ))}
          </Select>
         )}
        />
        {errors.type && (
         <Box sx={{ color: 'error.main', fontSize: '12px', mt: 0.5 }}>
          {errors.type.message}
         </Box>
        )}
       </FormControl>

       <FormControl fullWidth error={!!errors.title}>
        <Controller
         name="title"
         control={control}
         rules={{
          required: t('dashboard.widgetTitleRequired'),
          minLength: {
           value: 2,
           message: t('dashboard.titleMinLength'),
          },
          maxLength: {
           value: 50,
           message: t('dashboard.titleMaxLength'),
          },
         }}
         render={({ field }) => (
          <TextField
           {...field}
           id="title"
           type="text"
           placeholder={t('dashboard.enterWidgetTitle')}
           variant="outlined"
           error={!!errors.title}
           helperText={errors.title?.message}
           sx={{
            '& .MuiInputBase-root': {
             backgroundColor: (theme) => theme.palette.background.paper,
            },
           }}
          />
         )}
        />
       </FormControl>
      </Box>
      {/* // if not likuciai */}
      {selectedType !== 'asd' && (
       <DashboardWidgetModalDate
        control={control}
        setValue={setValue}
        errors={errors}
       />
      )}

      <Box
       sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
       }}
      >
       <Typography
        sx={{
         fontSize: '14px',
         color: (theme) => theme.palette.kemek.darkGray,
         fontWeight: 500,
        }}
       >
        {t('dashboard.title')}
       </Typography>

       {selectedType === 'driversProgress' && (
        <DriversProgressFilters
         control={control as Control<WidgetProgressProps>}
         setValue={setValue as UseFormSetValue<WidgetProgressProps>}
         errors={errors as FieldErrors<WidgetProgressProps>}
        />
       )}

       {selectedType === 'destPointNeto' && (
        <DestPointNetoFilters
         control={control as Control<WidgetDestPointNetoProps>}
         setValue={setValue as UseFormSetValue<WidgetDestPointNetoProps>}
         errors={errors as FieldErrors<WidgetDestPointNetoProps>}
        />
       )}

       {selectedType === 'InOutProduct' && (
        <InOutProductFilters
         control={control as Control<WidgetInOutProductProps>}
         setValue={setValue as UseFormSetValue<WidgetInOutProductProps>}
         errors={errors as FieldErrors<WidgetInOutProductProps>}
        />
       )}

       {selectedType === 'ProductStock' && (
        <ProductStockFilters
         control={control as Control<WidgetProductStockProps>}
         setValue={setValue as UseFormSetValue<WidgetProductStockProps>}
         errors={errors as FieldErrors<WidgetProductStockProps>}
        />
       )}
      </Box>
     </Box>
    </form>
   </DialogContent>

   <DialogActions
    sx={{ padding: '16px 24px', justifyContent: 'space-between' }}
   >
    <Box>
     {initialWidget && onDelete && (
      <Button
       variant="outlined"
       color="error"
       size="large"
       sx={{ display: { xs: 'none', md: 'flex' } }}
       endIcon={
        <IconDelete
         style={{
          color: th.palette.kemek.red,
          transition: 'all 0.3s ease-in-out',
         }}
        />
       }
       onClick={handleDelete}
      >
       {t('global.delete')}
      </Button>
     )}
    </Box>

    <Box sx={{ display: 'flex', gap: 1 }}>
     <Button
      type="button"
      onClick={handleCancel}
      size="large"
      variant="text"
      color="secondary"
     >
      {t('global.cancel')}
     </Button>
     <Button
      variant="contained"
      type="submit"
      form="dashboard-widget-form"
      size="large"
      onClick={handleSubmit(handleFormSubmit)}
     >
      {t('global.save')}
     </Button>
    </Box>
   </DialogActions>
  </Dialog>
 );
};
export default DashboardWidgetModal;

